import { JSX, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid2";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import AddIcon from "@mui/icons-material/Add";

import AlertBanner from "lib/components/alert/AlertBanner";
import AlertMessage from "lib/components/alert/AlertMessage";
import SplitButton from "lib/ui/button/SplitButton";

import Filter from "utils/Filter";
import { asTitle } from "utils/Utils";
import useFetch from "utils/fetch/useFetch";

import { UserRoleKey } from "organisation/OrganisationTypes";

import AddOrganisationUserDialog from "./AddOrganisationUserDialog";
import ImportOrganisationUsersDialog from "./ImportOrganisationUsersDialog";
import OrganisationUserClient from "./OrganisationUserClient";
import { Overview } from "./OrganisationUserTypes";
import OrganisationUsersRow from "./OrganisationUsersRow";

export type OrganisationUsersTableProps = {
	users: Array<Overview>;
	currentUser: number;
	orgId: number;
};

export default function OrganisationUsersTable(
	props: Readonly<OrganisationUsersTableProps>
): JSX.Element {
	const { t } = useTranslation();

	const { call } = useFetch();

	const [users, setUsers] = useState(props.users);
	const [filter, setFilter] = useState("");
	const [addDialog, setAddDialog] = useState(false);
	const [uploadDialog, setUploadDialog] = useState(false);

	const [alert, setAlert] = useState<AlertMessage>();

	const filtered = useMemo(
		() =>
			users
				.filter(u =>
					u.email.toLowerCase().includes(filter.toLowerCase())
				)
				.sort((a, b) => a.email.localeCompare(b.email)),
		[users, filter]
	);

	const actions = useMemo(
		() => [
			{
				action: "ADD",
				text: t("addObject", { object: "user" })
			},
			{
				action: "UPLOAD",
				text: t("importObject", { object: "user_other" })
			}
		],
		[]
	);

	const closeAlert = useCallback(() => setAlert(undefined), []);

	const openAddDialog = useCallback(() => setAddDialog(true), []);
	const closeAddDialog = useCallback(() => setAddDialog(false), []);

	const openUploadDialog = useCallback(() => setUploadDialog(true), []);
	const closeUploadDialog = useCallback(() => setUploadDialog(false), []);

	const addUser = useCallback((user: Overview) => {
		setUsers(prevState => prevState.concat(user));
		closeAddDialog();
	}, []);

	const addUsers = useCallback((addedUsers: Array<Overview>) => {
		setUsers(prevState => prevState.concat(addedUsers));
		closeUploadDialog();
	}, []);

	const deleteUser = useCallback(async (id: number): Promise<void> => {
		closeAlert();

		try {
			await call(OrganisationUserClient.delete(props.orgId, id));
			setUsers(prevState => prevState.filter(u => u.id !== id));
			setAlert(
				AlertMessage.success(
					t("deleteObjectSuccess", { object: "user" })
				)
			);
		} catch {
			setAlert(
				AlertMessage.error(t("deleteObjectError", { object: "user" }))
			);
		}
	}, []);

	const changeUserRole = useCallback(
		async (id: number, role: UserRoleKey): Promise<void> => {
			closeAlert();

			const data = {
				role
			};

			try {
				const updatedUser = await call(
					OrganisationUserClient.update(props.orgId, id, data)
				);
				setUsers(prevState =>
					prevState.map(user => (user.id === id ? updatedUser : user))
				);
				setAlert(
					AlertMessage.success(
						t("updateObjectSuccess", { object: "user" })
					)
				);
			} catch {
				setAlert(
					AlertMessage.error(
						t("updateObjectError", { object: "user" })
					)
				);
			}
		},
		[]
	);

	const handleAddButton = useCallback(
		(index: number): void => {
			if (actions[index].action === "ADD") {
				openAddDialog();
			} else if (actions[index].action === "UPLOAD") {
				openUploadDialog();
			}
		},
		[openAddDialog]
	);

	return (
		<>
			{alert && <AlertBanner alert={alert} onClose={closeAlert} />}

			<Grid container justifyContent="flex-end" columnGap={1}>
				<Grid>
					<Filter onChange={setFilter} />
				</Grid>
				<Grid>
					<SplitButton
						startIcon={<AddIcon />}
						options={actions.map(action => action.text)}
						onClick={handleAddButton}
					/>
				</Grid>
			</Grid>

			<Table>
				<TableHead>
					<TableRow>
						<TableCell />
						<TableCell sx={{ width: 140 }} />
						<TableCell padding="none" sx={{ width: 40 }} />
					</TableRow>
				</TableHead>
				<TableBody>
					{filtered.map(user => (
						<OrganisationUsersRow
							key={user.id}
							user={user}
							disabled={user.id === props.currentUser}
							onDelete={deleteUser}
							onChangeRole={changeUserRole}
						/>
					))}
				</TableBody>
			</Table>

			{addDialog && (
				<AddOrganisationUserDialog
					orgId={props.orgId}
					existingUsers={users}
					onClose={closeAddDialog}
					onCreated={addUser}
				/>
			)}

			{uploadDialog && (
				<ImportOrganisationUsersDialog
					orgId={props.orgId}
					existingUsers={users}
					onClose={closeUploadDialog}
					onImported={addUsers}
				/>
			)}
		</>
	);
}
